import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CTABand } from "@/components/home/HomeSections";
import { SERVICES } from "@/data/content";

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,102,255,0.15),transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Our services</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl" data-testid="services-page-title">
              Everything your business needs to <span className="text-gradient">build, run and scale</span>
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              One accountable team covering design, engineering, cloud, security and support — tailored for startups, small businesses and established organisations.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 2) * 0.08}>
              <article className="card-glow flex h-full flex-col rounded-2xl border border-white/10 bg-navy-900/60 p-7" data-testid={`services-detail-${s.slug}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5">
                    <s.icon className="h-5 w-5 text-cyan-300" />
                  </div>
                  <span className="font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="mt-5 font-display text-xl font-semibold tracking-tight text-white">{s.name}</h2>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                <ul className="mt-5 grid flex-1 gap-2 sm:grid-cols-2">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-[13px] text-slate-300">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-400" />
                      {d}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/5 pt-5">
                  <div className="flex flex-wrap gap-2">
                    {s.stack.map((t) => (
                      <span key={t} className="rounded-full bg-white/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">{t}</span>
                    ))}
                  </div>
                  <Link to={s.href} className="group flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-cyan-300" data-testid={`services-link-${s.slug}`}>
                    Explore <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <div className="mt-20">
          <SectionHeading
            eyebrow="Not sure where to start?"
            title="Tell us the outcome you need — we'll map the route"
            description="Most projects blend several disciplines. A short discovery call is usually all it takes to scope the right mix and give you a clear, fixed proposal."
            align="center"
            testid="services-closing"
          />
        </div>
      </section>
      <CTABand />
    </div>
  );
}
